import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {InputChangeEvent} from 'util/Types';

import ChangeStudyableAssignmentPermissionWarningBanner, {
  ChangeStudyableAssignmentPermissionOriginType,
} from './studyable/ChangeStudyableAssignmentPermissionWarningBanner';
import {useCanChangeStudyableAssignmentStudiesQuery} from './studyable/useCanChangeStudyableAssignmentQuery';
import {StudyAssociation} from './StudyableDAO';
import StudyAutoLinkingInput from './StudyAutoLinkingInput';
import StudySearchInput, {StudySearchControlledItemsProps, StudySearchItem} from './StudySearchInput';

export interface StudyAssociationInputProps {
  studySearchControlledItemsProps: StudySearchControlledItemsProps;
  invalidItemNames: Array<string>;
  isAutoLinkingChecked: boolean;
  isAutoLinkingDisabled: boolean;
  onAutoLinkingCheckedChange: (e: InputChangeEvent) => void;
  onAutoLinkingStudyChange: (studyId: string) => void;
  displayPermissionWarning?: boolean;
  // The study that the item will be linked to
  autoLinkingStudy?: {
    id: string;
    name: string;
  };
  // If false, the auto-linking study is locked to its initial value.
  // If true, the user can pick the auto-linking study from a dropdown.
  enableAutoLinkingSelection?: boolean;
}

export default function StudyAssociationInput({
  studySearchControlledItemsProps,
  invalidItemNames,
  isAutoLinkingChecked,
  isAutoLinkingDisabled,
  onAutoLinkingCheckedChange,
  onAutoLinkingStudyChange,
  displayPermissionWarning,
  autoLinkingStudy,
  enableAutoLinkingSelection = false,
}: StudyAssociationInputProps): JSX.Element {
  return (
    <>
      <StudySearchInput {...studySearchControlledItemsProps} shouldAllowMultiple={true} />
      {displayPermissionWarning && invalidItemNames.length > 0 && (
        <div className={css(styles.warningBanner)}>
          <ChangeStudyableAssignmentPermissionWarningBanner
            warningOriginType={ChangeStudyableAssignmentPermissionOriginType.STUDY}
            invalidStudyNames={invalidItemNames}
          />
        </div>
      )}
      <StudyAutoLinkingInput
        selectedStudies={studySearchControlledItemsProps.selectedItems}
        invalidItemNames={invalidItemNames}
        isAutoLinkingChecked={isAutoLinkingChecked}
        isAutoLinkingDisabled={isAutoLinkingDisabled}
        onAutoLinkingCheckedChange={onAutoLinkingCheckedChange}
        onAutoLinkingStudyChange={onAutoLinkingStudyChange}
        enableAutoLinkingSelection={enableAutoLinkingSelection}
        autoLinkingStudy={autoLinkingStudy}
        includeAutoLinkingStudyDropdown={BenchlingConfig.STUDIES_ENTRY_AUTO_LINKING_EXTENSIONS}
      />
    </>
  );
}

/**
 * If `enableAutoLinkingSelection` is true, the user can pick the auto-linking study from a dropdown.
 * Removing `initialAutoLinkingStudy` moves auto-linking to another selected study instead of keeping it locked to the removed one.
 */
export function useStudyAssociationInputProps({
  initialStudySearchItems,
  displayPermissionWarning,
  initialAutoLinkingStudy,
  enableAutoLinkingSelection = false,
}: {
  initialStudySearchItems: Array<StudySearchItem>;
  displayPermissionWarning?: boolean;
  initialAutoLinkingStudy?: {
    id: string;
    name: string;
  };
  enableAutoLinkingSelection?: boolean;
}): StudyAssociationInputProps {
  const hasInitialAutoLinkingStudy = initialAutoLinkingStudy !== undefined;
  const isAutoLinkingStudyLocked = hasInitialAutoLinkingStudy && !enableAutoLinkingSelection;
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>(initialStudySearchItems);
  const [autoLinkingStudyId, setAutoLinkingStudyId] = useState<string | undefined>(
    initialAutoLinkingStudy?.id ?? studySearchItems[0]?.id
  );
  const studyIds = studySearchItems.map((item) => item.id);
  // When enabledAutoLinkingSelection is false and an initialAutoLinkingStudy is set,
  // falls back to the initial study when the chosen one is deselected, so a locked study remains displaying when autoLinking becomes disabled.
  const autoLinkingStudy =
    studySearchItems.find((study) => study.id === autoLinkingStudyId) ??
    (isAutoLinkingStudyLocked ? initialAutoLinkingStudy : undefined);
  const isAutoLinkingStudySelected = autoLinkingStudy !== undefined && studyIds.includes(autoLinkingStudy.id);
  const [isAutoLinkingChecked, setIsAutoLinkingChecked] = useState(
    hasInitialAutoLinkingStudy && isAutoLinkingStudySelected
  );
  // Get studies missing the change studyable assignment permission
  const studyNamesToStudyablePermission = useCanChangeStudyableAssignmentStudiesQuery(studyIds);
  const invalidItemNames = Array.from(studyNamesToStudyablePermission.entries())
    .filter(([, hasPermission]) => !hasPermission)
    .map(([name]) => name);
  const isAutoLinkingDisabled =
    (isAutoLinkingStudyLocked && !isAutoLinkingStudySelected) || invalidItemNames.length > 0;

  const handleStudySearchItemsChange = (selectedStudies: Array<StudySearchItem>): void => {
    setStudySearchItems(selectedStudies);
    if (!selectedStudies.some((study) => study.id === autoLinkingStudyId)) {
      if (isAutoLinkingStudyLocked) {
        setIsAutoLinkingChecked(false);
      } else {
        setAutoLinkingStudyId(selectedStudies[0]?.id);
        if (selectedStudies.length === 0) {
          setIsAutoLinkingChecked(false);
        }
      }
    }
  };

  return {
    studySearchControlledItemsProps: {
      selectedItems: studySearchItems,
      onChange: handleStudySearchItemsChange,
    },
    invalidItemNames,
    isAutoLinkingChecked,
    isAutoLinkingDisabled,
    onAutoLinkingCheckedChange: (e: InputChangeEvent) => {
      setIsAutoLinkingChecked(e.target.checked);
    },
    onAutoLinkingStudyChange: setAutoLinkingStudyId,
    displayPermissionWarning,
    autoLinkingStudy,
    enableAutoLinkingSelection,
  };
}

export function getStudyAssociations(
  studyAssociationInputProps: StudyAssociationInputProps
): Array<StudyAssociation> {
  return studyAssociationInputProps.studySearchControlledItemsProps.selectedItems.map((item) => ({
    studyId: item.id,
    isAutoLinking:
      studyAssociationInputProps.isAutoLinkingChecked &&
      item.id === studyAssociationInputProps.autoLinkingStudy?.id,
  }));
}

const styles = StyleSheet.create({
  warningBanner: {
    marginTop: 5,
  },
});
